import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { capitalize } from '@attain-apps/string-utils'

import * as state from '../../../../../../state'
import * as errorMessage from '../../error-message'

/**
 * Modal used for creating and editing Admin Center users.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.input.text.Component,
    ds.overlay.centered.Component,
    ds.layout.widget.Component,
    errorMessage.Component,
  ],
  selector: 'atn-admin-user-modal',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.admin.users.Collection} */
  public readonly usersEc = ngCore.inject(state.admin.users.Collection)

  /** @see {@link state.admin.contexts.Feature} */
  private readonly _contextsFeature = ngCore.inject(
    state.admin.contexts.Feature,
  )

  /** Editing user */
  public readonly userSg = ngCore.computed(() =>
    this.usersEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal(),
  )

  /** Editing user name*/
  public readonly firstNameSg = ngCore.computed(
    (user = this.userSg()) => user?.firstName,
  )

  /** Editing user last name*/
  public readonly lastNameSg = ngCore.computed(
    (user = this.userSg()) => user?.lastName,
  )

  /** Editing user email*/
  public readonly emailSg = ngCore.computed(
    (user = this.userSg()) => user?.email,
  )

  /** Editing user job title*/
  public readonly jobTitleSg = ngCore.computed(
    (user = this.userSg()) => user?.jobTitle,
  )

  /** Whether the editing user is a Platform Admin */
  public readonly isPlatformAdminSg = ngCore.computed(
    (user = this.userSg()) => user?.isPlatformAdmin ?? false,
  )

  /** Whether the Platform Admin field should be displayed */
  public readonly showPlatformAdminFieldSg = ngCore.computed(
    (
      activeContext = this._contextsFeature.selectors.activeEntityContext.asSignal(),
    ) => activeContext === undefined,
  )

  /**
   * Shows dialog title based on {@link isNew}
   */
  public readonly titleSg = ngCore.computed(
    (
      isNew = this.usersEc.plugins.currentEntityPlugin.feature.selectors
        .isCreating()
        .asSignal(),
    ) => `${isNew ? 'Create' : 'Update'} User`,
  )

  /**
   * Updates a specific property of the currently edited user.
   */
  public updateUserPropertyHandler<K extends keyof ents.core.admin.User.Input>(
    prop: K,
    value: ents.core.admin.User.Input[K],
  ): void {
    this.usersEc.plugins.editingPlugin.feature.actions.edit
      .from(`User.Dialog.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: user => {
          if (!user) {
            return
          }
          user[prop] = value
        },
      })
  }

  /** Submit changes handler */
  public saveClickHandler = () => {
    this.usersEc.plugins.editingPlugin.feature.actions.submit
      .from('User.Dialog.SaveButton.Clicked')
      .dispatch({
        current: true,
        skipUnset: true,
      })
  }

  /** Discard changes handler */
  public cancelClickHandler = () => {
    this.usersEc.plugins.editingPlugin.feature.actions.discard
      .from('User.Dialog.CancelButton.Clicked')
      .dispatch({
        current: true,
        skipUnset: true,
      })
  }
}
